/**
 * MeshGeometry - Convert SurfaceNet output to renderable geometry data
 *
 * The mesh is INDEXED: the mesher's shared vertices are the geometry's vertices, and its triangles
 * are the index buffer. It used to be expanded to one vertex per triangle corner so that each corner
 * could carry a one-hot barycentric weight and the triangle's three material ids — 9M vertices a
 * frame for 3M triangles, and the vertex stream was the frame on an integrated GPU (see
 * vertexFormat.ts for the numbers).
 *
 * THE SLOT RULE makes sharing possible. A vertex gets a permanent slot from its cell,
 * (x + y + z) mod 3, and the blend only needs a triangle's three vertices to occupy three DIFFERENT
 * slots. SurfaceNets emits a quad around each crossing edge joining cells c, c-u, c-v, c-u-v — with
 * coordinate sums S, S-1, S-1, S-2 — and splits it along the c ↔ c-u-v diagonal (SurfaceNet.ts, the
 * idx0–idx3 edge of both triangles), so every triangle is {S, S-1, S-2}: three distinct slots, always,
 * with no change to the mesher. indexedMaterialSlots.test.ts guards it.
 *
 * Per vertex: materialWeights = one-hot(slot) (interpolates to the barycentric, as before) and
 * materialIds = its own id in its own slot, 0 elsewhere. Interpolated, slot k's id is id_k * w_k, and
 * the fragment recovers id_k = idw[k] / w[k] (terrainShaders recoverMaterialIds). Aprons are built
 * per corner (SeamStitcher) and use the same encoding.
 *
 * Split into two layers:
 * - expandGeometry(): Pure function (no Three.js) — runs in worker or main thread. The name is
 *   historical; it no longer expands anything.
 * - createBufferGeometry(): Wraps raw arrays into THREE.BufferGeometry (main thread only)
 */

import * as THREE from 'three';
import { VOXEL_SCALE, CHUNK_SIZE, MESH_MARGIN, GRID_SIZE } from '@worldify/shared';
import {
  SurfaceNetOutput, sampleCellLight, unpackSkyLight, unpackBlockLight,
} from './SurfaceNet.js';
import {
  TERRAIN_ATTRS, MATERIAL_SLOT_STRIDE, bufferAttribute, unorm8, type AttrArray,
} from './vertexFormat.js';

/**
 * Conservative local-space extent of a chunk mesh, in world units. SurfaceNets places
 * vertices at grid coords in [0, CHUNK_SIZE+MESH_MARGIN] × VOXEL_SCALE (never negative),
 * so this box always contains the geometry. Used to set bounds without a per-remesh
 * vertex scan (computeBoundingBox/Sphere iterate every vertex, on the main thread).
 */
const CHUNK_MESH_EXTENT = (CHUNK_SIZE + MESH_MARGIN) * VOXEL_SCALE;
const CHUNK_BOUND_CENTER = CHUNK_MESH_EXTENT / 2;
// Half-diagonal of the extent cube — radius from center to a far corner.
const CHUNK_BOUND_RADIUS = Math.sqrt(3) * CHUNK_BOUND_CENTER;
/**
 * Raw mesh data - plain typed arrays, no Three.js dependency.
 * This is what gets transferred between worker and main thread. ("Expanded" is historical: the
 * arrays are per SHARED vertex now, see the file header.)
 */
export interface ExpandedMeshData {
  positions: Float32Array;
  normals: Float32Array;
  /** MATERIAL_SLOT_STRIDE bytes per vertex: three integer material ids + a pad (vertexFormat.ts). */
  materialIds: Uint8Array;
  /** MATERIAL_SLOT_STRIDE bytes per vertex: a one-hot basis vector, 255 = 1.0, normalized. */
  materialWeights: Uint8Array;
  /** One byte per vertex, 0..255 for 0..1 (normalized). */
  lightLevels: Uint8Array;
  blockLightLevels: Uint8Array;
  /** Wrap share per vertex (0..1 as a byte) — the shaders pin a welded rim by it. See SurfaceNetOutput. */
  shoreFades: Uint8Array;
  /** Per-vertex grid cell index (for re-sampling light without re-meshing). Not a GPU
   *  attribute — kept on the main thread for the light-only relight path. */
  cellIndices: Uint16Array;
  /** Triangle list into the vertex arrays (the mesher's own triangles). */
  indices: Uint32Array;
  /**
   * Boundary (seam) vertices grouped by chunk face, for normal reconciliation.
   * CSR layout: `indices` holds vertex indices; face `f`'s slice is
   * `indices[faceOffsets[f] .. faceOffsets[f+1]]`. Face order:
   * [lowX, highX, lowY, highY, lowZ, highZ] (matches SurfaceNet boundaryFlags bits).
   */
  boundary: { indices: Uint32Array; faceOffsets: Uint32Array };
}

/**
 * Which material slot a vertex owns, from its cell: (x + y + z) mod 3. See the file header for why
 * this is distinct across every SurfaceNets triangle.
 */
export function materialSlot(cell: number): number {
  const x = cell % GRID_SIZE;
  const y = ((cell / GRID_SIZE) | 0) % GRID_SIZE;
  const z = (cell / (GRID_SIZE * GRID_SIZE)) | 0;
  return (x + y + z) % 3;
}

/**
 * Convert SurfaceNet output to attribute arrays — one vertex per mesher vertex, slot-encoded materials.
 * Pure function — no Three.js dependency. Safe to run in a worker.
 *
 * @param output SurfaceNet mesh data with shared vertices and per-vertex materials
 * @param scale Grid-unit → world-unit factor. VOXEL_SCALE for LOD 0; VOXEL_SCALE * 2^lod for
 *   coarse chunks — the LOD factor is BAKED into vertex positions (never object.scale) because
 *   the TerrainBatch instance matrix is translate-only.
 * @returns Mesh data, or null if empty
 */
export function expandGeometry(output: SurfaceNetOutput, scale: number = VOXEL_SCALE): ExpandedMeshData | null {
  const triangleCount = output.triangleCount;
  const vertexCount = output.vertexCount;
  if (triangleCount === 0 || vertexCount === 0) return null;

  // Every mesher vertex is in the geometry — including the edge-layer ones no triangle uses, which a
  // cross-LOD seam welds to (SeamStitcher looks them up by cell). The expansion used to append those
  // separately; sharing carries them for free.
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const materialIds = new Uint8Array(vertexCount * MATERIAL_SLOT_STRIDE);
  const materialWeights = new Uint8Array(vertexCount * MATERIAL_SLOT_STRIDE);
  const lightLevels = new Uint8Array(vertexCount);
  const blockLightLevels = new Uint8Array(vertexCount);
  const shoreFades = new Uint8Array(vertexCount);
  const cellIndices = new Uint16Array(vertexCount);
  // The mesher's buffers are pooled, so their length is capacity: copy the used prefix.
  const indices = output.indices.slice(0, triangleCount * 3);

  // Per-face buckets of vertex indices that sit on a chunk boundary plane.
  // Order matches SurfaceNet boundaryFlags bits: [lowX, highX, lowY, highY, lowZ, highZ].
  const srcFlags = output.boundaryFlags;
  const faceBuckets: number[][] = [[], [], [], [], [], []];

  for (let v = 0; v < vertexCount; v++) {
    const p = v * 3;
    positions[p] = output.positions[p] * scale;
    positions[p + 1] = output.positions[p + 1] * scale;
    positions[p + 2] = output.positions[p + 2] * scale;
    normals[p] = output.normals[p];
    normals[p + 1] = output.normals[p + 1];
    normals[p + 2] = output.normals[p + 2];

    // The slot rule: this vertex's id in its own slot, one-hot weight in the same slot, 0 elsewhere.
    const cell = output.cellIndices[v];
    const slot = materialSlot(cell);
    const sb = v * MATERIAL_SLOT_STRIDE;
    materialIds[sb + slot] = output.materials[v];
    materialWeights[sb + slot] = 255;

    lightLevels[v] = unorm8(output.lights[v]);
    blockLightLevels[v] = unorm8(output.blockLights[v]);
    shoreFades[v] = unorm8(output.shoreFade[v]);
    cellIndices[v] = cell;

    const flag = srcFlags[v];
    if (flag !== 0) {
      for (let f = 0; f < 6; f++) if (flag & (1 << f)) faceBuckets[f].push(v);
    }
  }

  // Flatten the 6 buckets into a single CSR array + length-7 offsets.
  const faceOffsets = new Uint32Array(7);
  let total = 0;
  for (let f = 0; f < 6; f++) { faceOffsets[f] = total; total += faceBuckets[f].length; }
  faceOffsets[6] = total;
  const boundaryIndices = new Uint32Array(total);
  let w = 0;
  for (let f = 0; f < 6; f++) {
    const b = faceBuckets[f];
    for (let i = 0; i < b.length; i++) boundaryIndices[w++] = b[i];
  }

  return {
    positions, normals, materialIds, materialWeights, lightLevels, blockLightLevels, shoreFades,
    cellIndices, indices,
    boundary: { indices: boundaryIndices, faceOffsets },
  };
}

/**
 * Wrap raw mesh data into a THREE.BufferGeometry.
 * Main thread only — lightweight, just setAttribute calls.
 * Attribute layout is TERRAIN_ATTRS (vertexFormat.ts).
 *
 * @param lod The chunk's LOD level — scales the fixed conservative bounds by 2^lod, matching the
 *   world-scale baked into the vertex positions by expandGeometry.
 */
export function createBufferGeometry(data: ExpandedMeshData, lod: number = 0): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const arrays: Record<string, AttrArray> = {
    position: data.positions, normal: data.normals,
    materialIds: data.materialIds, materialWeights: data.materialWeights,
    lightLevel: data.lightLevels, blockLight: data.blockLightLevels, shoreFade: data.shoreFades,
  };
  for (const def of TERRAIN_ATTRS) geometry.setAttribute(def.name, bufferAttribute(def, arrays[def.name]));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  // Fixed conservative bounds — avoids the per-remesh vertex scan of
  // computeBoundingBox/computeBoundingSphere. The chunk mesh always fits this box.
  const extent = CHUNK_MESH_EXTENT * (1 << lod);
  const center = CHUNK_BOUND_CENTER * (1 << lod);
  const radius = CHUNK_BOUND_RADIUS * (1 << lod);
  geometry.boundingBox = new THREE.Box3(
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(extent, extent, extent),
  );
  geometry.boundingSphere = new THREE.Sphere(
    new THREE.Vector3(center, center, center),
    radius,
  );
  return geometry;
}

/**
 * Rewrite ONLY the `lightLevel` + `blockLight` vertex attributes of a geometry, re-reading each
 * vertex's cell from an expanded GRID_SIZE³ grid (via the per-vertex `cellIndices`). No SurfaceNets, no
 * geometry realloc — valid only when the geometry's voxels are unchanged so the stored cells still
 * describe it. Shared by ChunkGeometry (committed chunks) and the build-preview meshes.
 *
 * @returns true if the geometry had the two light attributes (and was rewritten).
 */
export function resampleLightAttributes(
  geometry: THREE.BufferGeometry,
  cellIndices: Uint16Array,
  grid: Uint32Array,
): boolean {
  const skyAttr = geometry.getAttribute('lightLevel') as THREE.BufferAttribute | undefined;
  const blockAttr = geometry.getAttribute('blockLight') as THREE.BufferAttribute | undefined;
  if (!skyAttr || !blockAttr) return false;

  // Normalized bytes (vertexFormat.ts): the shader reads 0..255 as 0..1.
  const sky = skyAttr.array as Uint8Array;
  const block = blockAttr.array as Uint8Array;
  const count = cellIndices.length;
  for (let v = 0; v < count; v++) {
    const packed = sampleCellLight(grid, cellIndices[v]);
    sky[v] = unorm8(unpackSkyLight(packed));
    block[v] = unorm8(unpackBlockLight(packed));
  }
  skyAttr.needsUpdate = true;
  blockAttr.needsUpdate = true;
  return true;
}

/**
 * Create a BufferGeometry from SurfaceNet output with material blending support.
 * Convenience wrapper: expandGeometry + createBufferGeometry in one call.
 * Used by sync path (fallback) and existing callers.
 */
export function createGeometryFromSurfaceNet(output: SurfaceNetOutput): THREE.BufferGeometry {
  const data = expandGeometry(output);
  if (!data) return new THREE.BufferGeometry();
  return createBufferGeometry(data);
}
